// @flow
import * as React from 'react'
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import ChoosePlan from './ChoosePlan'
import styled from 'styled-components'
import PostSubContent from './PostSubContent'
import PaymentRequestButton from './PaymentRequestButton'
import { isServer } from '../../../../env'
import {
  CardBottomRowItem,
  CardNumberContainer,
  colors,
  CTAPrimaryOrange,
  ErrorBodyText,
  Form,
  OutlineOnFocusVisible
} from '@components/shared'
import { ModalContainer, ModalDescription, ModalHeader } from '@components/subscribe/SubscriptionModal/paywallStyles'
import BackIcon from '@components/shared/icons/BackIcon'
import XIcon from '@components/shared/icons/XIcon'
import SubscriptionName from '@components/subscribe/SubscriptionName'
import PromoCode from '@components/PromoCode'
import { config } from 'react-awesome-styled-grid'
import type { SubscriptionLengthType } from '../../../../flowTypes'
import BackgroundGradient from '@components/BackgroundGradient'

export type PropsType = {
  headerText: string,
  subHeaderText?: string,
  isExpiring: boolean,
  isSubscribed: boolean,
  inProcess: boolean,
  userLoaded: boolean,
  plan?: SubscriptionLengthType,
  error?: string,
  subscribeText: string,
  price: string,
  successfulPurchase?: boolean,
  isOnPayment: boolean,
  isFullScreen?: boolean,
  navigateToPostSubscribe?: {},
  promoValid?: boolean,
  isChurned: boolean,
  isPromoFlow?: boolean,
  isHunkUrl: boolean,
  trialDays: number
}

export type DispatchPropsType = {
  subscribe: (
    stripeObj: any,
    cardNumberElement?: any,
    altPaymentEvent?: any,
    navigateToPostSubscribe?: any
  ) => void,
  goBackToPlanSelection: () => void,
  openHomePage: () => void,
  applyPromo: (promo: string) => void
}
export const defaultStripeStyles = {
  style: {
    base: {
      iconColor: colors.white,
      color: colors.white,
      fontFamily: 'sans-serif',
      fontSize: '16px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontSmoothing: 'antialiased',
      ':-webkit-autofill': {
        color: colors.white
      },
      '::placeholder': {
        color: colors.white70
      }
    },
    invalid: {
      iconColor: colors.red,
      color: colors.red
    }
  }
}

export default function Paywall (
  props: PropsType & DispatchPropsType
): React.Node {
  const {
    headerText,
    subHeaderText,
    isExpiring,
    isSubscribed,
    inProcess,
    userLoaded,
    error,
    subscribeText,
    subscribe,
    successfulPurchase,
    isOnPayment,
    isFullScreen,
    goBackToPlanSelection,
    isChurned,
    openHomePage,
    isPromoFlow,
    isHunkUrl
  } = props

  const [churned, setChurned] = React.useState(isChurned)
  const [submitted, setSubmitted] = React.useState(false)

  React.useEffect((): void => {
    setChurned(isChurned)
  }, [])

  React.useEffect((): void => {
    if (submitted && error) {
      setSubmitted(false)
    }
  }, [error])

  React.useEffect((): void => {
    if (isPromoFlow || isHunkUrl) {
      props.applyPromo('legacyannualprice')
    }
  }, [isOnPayment, isPromoFlow, isHunkUrl])

  const elements = useElements()
  const stripe = useStripe()

  const handleSubscribe = (
    stripeObj: any,
    cardNumberElement?: any,
    altPaymentEvent?: any,
    navigateToPostSubscribe?: any
  ): void => {
    if (submitted) {
      return
    }
    setSubmitted(true)
    subscribe(
      stripeObj,
      cardNumberElement,
      altPaymentEvent,
      navigateToPostSubscribe
    )
  }

  const submit =
    (e: any): void => {
      e.preventDefault()
      if (submitted) {
        return
      }
      if (!isServer && stripe) {
        // injectStripe adds a stripe elements prop, and we'll add it to the window object so we can reference it in services/stripe.js
        window.stripeObj = stripe
      }
      const cardNumberElement = elements.getElement(CardNumberElement)
      setSubmitted(true)
      subscribe(stripe, cardNumberElement, null, props.navigateToPostSubscribe)
    }

  let subscribeButtonText = props.promoValid
    ? 'SUBSCRIBE NOW'
    : props.plan === 'monthly'
      ? 'START MONTHLY PLAN'
      : 'START 7-DAY FREE TRIAL'
  if (isExpiring) {
    subscribeButtonText = 'RENEW SUBSCRIPTION'
  }

  const altPaymentSubmit = (altPaymentEvent: any): void => {
    handleSubscribe(stripe, null, altPaymentEvent, props.navigateToPostSubscribe)
  }

  if (isServer || !userLoaded) {
    return <div />
  }

  if (isSubscribed && !isExpiring) {
    if (churned) {
      return (
        <ModalContainer>
          <img
            src="/images/check-icon-white.png"
            width="40"
            height="40"
            style={{ margin: '0 0 20px 0' }}
          />
          <ModalHeader>Welcome back</ModalHeader>
          <ModalDescription>{`Your subscription with Dipsea is good to go`}</ModalDescription>
        </ModalContainer>
      )
    } else {
      return (
        <SubscriptionModalContainer isBlue={isOnPayment || isSubscribed}>
          <PostSubContent />
        </SubscriptionModalContainer>
      )
    }
  }

  const TermsAndPolicy = (
    <TermsContainer isOnPayment={isOnPayment}>
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.dipseastories.com/terms/dark"
      >
        Terms & Conditions
      </a>
      {' • '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.dipseastories.com/privacypolicy/dark"
      >
        Privacy
      </a>
      {' • '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.dipseastories.com/paymentterms/dark/"
      >
        Payment Terms
      </a>
    </TermsContainer>
  )

  return (
    <SubscriptionModalContainer>
      {!!isFullScreen && (
        <TopNav>
          <BackContainer>
            {isOnPayment && (
              <NavButton aria-label="Back" onClick={goBackToPlanSelection}>
                <BackIcon
                  color={colors.white}
                  size={26}
                />
              </NavButton>
            )}
          </BackContainer>

          <NavContainer>
            <NavButton
              aria-label="Close"
              onClick={openHomePage}
            >
              <XIcon
                size={23}
                color={colors.white}
              />
            </NavButton>
          </NavContainer>
        </TopNav>
      )}

      {!isOnPayment
        ? (
        <>
          <BackgroundImageContainer isFullScreen={isFullScreen}>
            <BackgroundGradient quadrant={2} overlayColor='orange' />
          </BackgroundImageContainer>

          <ChoosePlan header={headerText} subHeader={subHeaderText} isPromoFlowPrice={isPromoFlow || isHunkUrl} />
          {TermsAndPolicy}
        </>
          )
        : (
        <>
          <BackgroundImageContainer isFullScreen={isFullScreen}>
            <BackgroundGradient quadrant={2} overlayColor='blue' />
          </BackgroundImageContainer>

          <SubModalPaymentContainer>
            <Form padding="0">
              <CardInputContainer>
                <SectionTitle>Credit Card</SectionTitle>
                <ErrorContainer>
                  <ErrorBodyText showError={!!error}>{error}</ErrorBodyText>
                </ErrorContainer>

                <SubscriptionNameContainer>
                  <SubscriptionName />
                </SubscriptionNameContainer>
                <CardNumberContainer>
                  <CardNumberElement
                    options={{
                      ...defaultStripeStyles,
                      placeholder: 'Card Number'
                    }}
                  />
                </CardNumberContainer>
                <CardBottomRow>
                  <CardBottomRowItem margin={'0 7.5px 0 0'}>
                    <CardExpiryElement options={defaultStripeStyles} />
                  </CardBottomRowItem>
                  <CardBottomRowItem margin={'0 0 0 5.5px'}>
                    <CardCvcElement options={defaultStripeStyles} />
                  </CardBottomRowItem>
                </CardBottomRow>
              </CardInputContainer>

              <PromoCode />

              <CTAPrimaryOrange
                className="tc-checkout-submit"
                type="submit"
                onClick={submit}
                disabled={inProcess || successfulPurchase || submitted}
              >
                {inProcess ? 'Processing' : subscribeButtonText}
              </CTAPrimaryOrange>

              <PaymentRequestButton
                stripe={stripe}
                price={props.price}
                trialDays={props.trialDays}
                plan={props.plan}
                subscribe={(altPaymentEvent: any): void =>
                  altPaymentSubmit(altPaymentEvent)
                }
              />
            </Form>
            {TermsAndPolicy}
            <PaymentTerms>{subscribeText}</PaymentTerms>
          </SubModalPaymentContainer>
        </>
          )}
    </SubscriptionModalContainer>
  )
}

const BackgroundImageContainer = styled.div`
  position: fixed;
  z-index: -1;
  top: 0;
  width: 100%;
  height: 100%;
  ${(props: any): string => config(props).media.md`
    position: ${(props: { isFullScreen: boolean }): string => props.isFullScreen ? 'fixed' : 'absolute'};
  `};
`
const TopNav = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  margin: 20px 20px 0;
  ${(props: any): string => config(props).media.md`
    margin: 70px 70px 0;
  `};
`
const ErrorContainer = styled.div`
  height: 20px;
  display: flex;
  justify-content: flex-end;
`
const SectionTitle = styled.h1`
  color: ${colors.white};
  font-family: "Good Sans Bold";
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 150%;
  margin-block: 0;
  margin: 0;
  padding: 0;
  ${(props: any): string => config(props).media.lg`
    font-size: 20px;
  `}
`
const PaymentTerms = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  color: ${colors.white};
  font-family: "Good Sans Regular";
  font-style: normal;
  font-weight: normal;
  font-size: 10px;
  line-height: 150%;
  margin: 0 auto;
  text-align: center;
`
const SubscriptionNameContainer = styled.div`
  width: 100%;
  display: flex;
`
const CardInputContainer = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  margin: 0 0 12px 0;
  position: relative;
`
const CardBottomRow = styled.div`
  display: flex;
  flex-direction: row;
`
const SubscriptionModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`
const SubModalPaymentContainer = styled.div`
  max-width: 414px;
  padding: 0 20px;
  margin: 90px 0;

  ${(props: any): string => config(props).media.md`
      margin: 62px 0;
  `};
`
const TermsContainer = styled.div`
  font-family: "Good Sans Light";
  font-style: normal;
  font-weight: bold;
  font-size: 12px;
  line-height: 16px;
  padding: ${(props: any): any =>
    props.isOnPayment ? '20px 0 20px' : '1px 0 72px'};
  text-align: center;
  color: ${colors.white};
  a {
    color: ${colors.white};
    margin: 0 0.1em;
    text-decoration: underline;
    :hover {
      opacity: 0.5;
    }
    outline: none;
    :focus {
      box-shadow: 0 0 0 1.25pt ${colors.white};
      border-radius: 8px;
    }
  }
`
const NavButton = styled.button`
  align-items: center;
  background-color: transparent;
  border: none;
  border-radius: 40px;
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  margin: 0;
  outline: none;
  padding: 0;
  width: 40px;
  z-index: 1001;
  :hover {
    opacity: 0.8;
  }

  ${OutlineOnFocusVisible};
`

const NavContainer = styled.div`
  margin-right: -7px;
`
const BackContainer = styled.div`
  margin-left: -12px;
`
